import {
  CLOSURE_DESCRIPTION_MIN_LENGTH,
  DESCRIPTION_REQUIRED_MESSAGE,
  TICKET_MESSAGE_ERROR_CODES,
  TICKET_STATE_LABELS,
  citedDescription,
  isDescriptionMissing,
  type ResolvedTicket,
} from '@easyhelp/shared';
import type { ApiError } from '../../api/client';

/**
 * Testi di «Chiudi il ticket dopo l'invio» nel composer (story #6): con l'opzione spuntata il testo
 * è la **descrizione della correzione** e parte dentro l'email di chiusura delle Impostazioni.
 */
export const RESOLVE_COPY = {
  label: 'Descrizione della correzione',
  placeholder: 'Cosa è stato corretto, scritto per chi ha segnalato: entra nell’email di chiusura.',
  hint: 'Parte come email di chiusura a chi ha segnalato, nel testo delle Impostazioni, e il ticket passa a Risolto. Senza descrizione, l’email dichiara che non ci sono dettagli.',
  submit: 'Invia e chiudi',
} as const;

/**
 * Perché «Invia e chiudi» non è disponibile con questo testo, oppure `null`: la regola
 * «Descrizione obbligatoria alla chiusura» (la applica comunque l'API, 422) sulla descrizione che
 * l'API citerebbe: il testo scritto o, se vuoto, la correzione registrata. Un testo vuoto senza
 * correzione e con la regola disattivata è ammesso.
 */
export function resolveBlockedReason(
  text: string,
  correction: { text: string } | null,
  settings: { requireDescriptionOnClose: boolean } | undefined
): string | null {
  const cited = citedDescription(text, correction);
  if (!settings || !isDescriptionMissing(cited, settings)) return null;
  const length = cited.length;
  return length === 0
    ? DESCRIPTION_REQUIRED_MESSAGE
    : `${DESCRIPTION_REQUIRED_MESSAGE} Mancano ${CLOSURE_DESCRIPTION_MIN_LENGTH - length} caratteri.`;
}

/** Esito di «Invia e chiudi», sotto il pulsante. */
export function resolveOutcome(resolved: ResolvedTicket): { severity: 'success' | 'warning'; text: string } {
  const { entry } = resolved;
  if (resolved.stateChangedMeanwhile) {
    return {
      severity: 'warning',
      text: `Email di chiusura inviata a ${entry.recipient}, ma nel frattempo il ticket è passato a «${TICKET_STATE_LABELS[resolved.state]}»: lo stato non è cambiato.`,
    };
  }
  return {
    severity: 'success',
    text: resolved.correction
      ? `Email di chiusura inviata a ${entry.recipient} con la correzione. Il ticket è «${TICKET_STATE_LABELS[resolved.state]}».`
      : `Email di chiusura inviata a ${entry.recipient}, senza dettagli sulla correzione. Il ticket è «${TICKET_STATE_LABELS[resolved.state]}».`,
  };
}

/** Errore di «Invia e chiudi»: il testo resta nel campo; nulla è stato inviato salvo dove detto. */
export function resolveErrorMessage(error: ApiError): string {
  if (error.code === TICKET_MESSAGE_ERROR_CODES.replyNotRecorded) {
    return "L'email di chiusura è partita ma la chiusura non è stata registrata: non reinviarla. Ricarica il ticket più tardi e, se non risulta chiuso, segnala il problema a chi gestisce la console.";
  }
  if (error.code === TICKET_MESSAGE_ERROR_CODES.replyInProgress) {
    return `${error.message} Il testo resta qui: non reinviarlo.`;
  }
  return `Ticket non chiuso: ${error.message} Il testo è ancora qui.`;
}
